import React, { useEffect, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { navigateScroll, navigateToTop } from "../../utils/Constants";
import { useAuth0 } from "@auth0/auth0-react";
import { requestAuth0PasswordResetEmail } from "../../utils/auth0";
import {
    INTERVIEW_ROLE_OPTIONS,
    getInterviewRoleLabel,
    normalizeInterviewRole,
} from "../../utils/interviewRoles";
import { API_BASE } from "../../config/apiBase";
import "../../styles/pages/auth/_profile.scss";

// ─── Icons ────────────────────────────────────────────────────────────────────
const IconClose = () => (
    <svg
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
        aria-hidden="true"
    >
        <path
            d="M3 3l10 10M13 3L3 13"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
        />
    </svg>
);

const IconChevRight = () => (
    <svg
        width="13"
        height="13"
        viewBox="0 0 13 13"
        fill="none"
        aria-hidden="true"
    >
        <path
            d="M4 2.5l4.5 4-4.5 4"
            stroke="currentColor"
            strokeWidth="1.4"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

const IconCheck = () => (
    <svg
        width="14"
        height="14"
        viewBox="0 0 14 14"
        fill="none"
        aria-hidden="true"
    >
        <path
            d="M2.5 7l3.5 3.5L11.5 4"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

// ─── Nav sections ─────────────────────────────────────────────────────────────
const BASE_SECTIONS = [
    { id: "personal-information", label: "Personal information" },
    { id: "account-settings", label: "Account settings" },
    { id: "danger-zone", label: "Danger zone" },
];

const RESET_REQUEST_KEY = "mooz:passwordResetRequestedAt";
const RESET_COOLDOWN_MS = 5 * 60 * 1000;

// ─── Field row ────────────────────────────────────────────────────────────────
function FieldRow({
    label,
    value,
    hint,
    onEdit,
    editable = true,
    actionLabel,
    actionNode,
    hideEmpty = false,
    danger = false,
    disabled = false,
}) {
    const showValue = value || (!hideEmpty && !value);
    return (
        <div className={`pf-field${danger ? " pf-field--danger" : ""}`}>
            <div className="pf-field__left">
                <span className="pf-field__label">{label}</span>
                {hint && <span className="pf-field__hint">{hint}</span>}
            </div>
            <div className="pf-field__value">
                {showValue ? (
                    value || <span className="pf-field__empty">Not set</span>
                ) : (
                    <span />
                )}
            </div>
            {actionNode
                ? actionNode
                : editable &&
                  onEdit && (
                      <button
                          type="button"
                          className={`pf-field__btn${danger ? " pf-field__btn--danger" : ""}`}
                          onClick={onEdit}
                          disabled={disabled}
                      >
                          {actionLabel || (danger ? "Delete" : "Edit")}
                      </button>
                  )}
            {!editable && <span />}
        </div>
    );
}

// ─── Plan badge ───────────────────────────────────────────────────────────────
function PlanBadge({ plan }) {
    const isPro =
        String(plan || "")
            .toLowerCase()
            .replace(/[\s-]/g, "_") === "pro_ai";
    return (
        <span className={`pf-plan-badge${isPro ? " pf-plan-badge--pro" : ""}`}>
            {isPro ? "Pro AI" : "Free"}
        </span>
    );
}

// ─── Toast ────────────────────────────────────────────────────────────────────
function Toast({ message, onDismiss }) {
    if (!message) return null;
    return (
        <div
            className={`pf-toast pf-toast--${message.type}`}
            role="alert"
            aria-live="polite"
        >
            {message.type === "success" && <IconCheck />}
            <span>{message.text}</span>
            <button
                type="button"
                className="pf-toast__close"
                onClick={onDismiss}
                aria-label="Dismiss"
            >
                <IconClose />
            </button>
        </div>
    );
}

// ─── Edit modal ───────────────────────────────────────────────────────────────
function EditModal({ field, profile, onClose, onSave }) {
    const [form, setForm] = useState({});
    const [codeSent, setCodeSent] = useState(false);
    const [error, setError] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (field === "name")
            setForm({
                firstName: profile.firstName,
                lastName: profile.lastName,
            });
        if (field === "email")
            setForm({
                newEmail: "",
                currentPassword: "",
                verificationCode: "",
            });
        if (field === "danger-zone") setForm({ currentPassword: "" });
        if (field === "role") setForm({ role: normalizeInterviewRole(profile.role) });
    }, [field, profile]);

    // Close on Escape
    useEffect(() => {
        const handler = (e) => {
            if (e.key === "Escape") onClose();
        };
        window.addEventListener("keydown", handler);
        return () => window.removeEventListener("keydown", handler);
    }, [onClose]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const sendVerificationCode = async () => setCodeSent(true);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        let payload = null;

        if (field === "name") {
            if (!form.firstName?.trim())
                return setError("First name is required.");
            payload = {
                firstName: form.firstName.trim(),
                lastName: (form.lastName || "").trim(),
            };
        }
        if (field === "role") {
            payload = { role: form.role };
        }
        if (field === "email") {
            if (!codeSent) return sendVerificationCode();
            if (!form.verificationCode?.trim())
                return setError("Enter the code we emailed you.");
            if (!form.newEmail?.trim())
                return setError("Enter a valid email address.");
            payload = {
                email: form.newEmail,
                password: form.currentPassword,
                verificationCode: form.verificationCode,
            };
        }
        if (field === "danger-zone") {
            if (!form.currentPassword?.trim())
                return setError("Enter your password to confirm deletion.");
            payload = { delete: true, password: form.currentPassword };
        }

        if (!payload) return;
        setSubmitting(true);
        await onSave(field, payload);
        setSubmitting(false);
    };

    const TITLES = {
        name: "Edit name",
        role: "Edit role",
        email: "Change email",
        "danger-zone": "Delete account",
    };

    return (
        <div
            className="pf-modal-overlay"
            role="dialog"
            aria-modal="true"
            aria-label={TITLES[field]}
        >
            <div
                className="pf-modal-backdrop"
                onClick={onClose}
                aria-hidden="true"
            />
            <div className="pf-modal">
                <div className="pf-modal__header">
                    <h2 className="pf-modal__title">{TITLES[field]}</h2>
                    <button
                        type="button"
                        className="pf-modal__close"
                        onClick={onClose}
                        aria-label="Close"
                    >
                        <IconClose />
                    </button>
                </div>

                <form className="pf-modal__form" onSubmit={handleSubmit}>
                    {field === "name" && (
                        <div className="pf-modal__fields">
                            <label
                                className="pf-modal__label"
                                htmlFor="firstName"
                            >
                                First name
                            </label>
                            <input
                                id="firstName"
                                className="pf-modal__input"
                                name="firstName"
                                value={form.firstName || ""}
                                onChange={handleChange}
                                placeholder="First name"
                                autoFocus
                            />
                            <label
                                className="pf-modal__label"
                                htmlFor="lastName"
                            >
                                Last name
                            </label>
                            <input
                                id="lastName"
                                className="pf-modal__input"
                                name="lastName"
                                value={form.lastName || ""}
                                onChange={handleChange}
                                placeholder="Last name"
                            />
                        </div>
                    )}

                    {field === "role" && (
                        <div className="pf-modal__fields">
                            <label className="pf-modal__label" htmlFor="role">
                                Role you're preparing for
                            </label>
                            <select
                                id="role"
                                className="pf-modal__input"
                                name="role"
                                value={form.role || ""}
                                onChange={handleChange}
                                autoFocus
                            >
                                <option value="">Select a role…</option>
                                {INTERVIEW_ROLE_OPTIONS.map((role) => (
                                    <option key={role.value} value={role.value}>
                                        {role.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    {field === "email" && !codeSent && (
                        <div className="pf-modal__fields">
                            <label
                                className="pf-modal__label"
                                htmlFor="newEmail"
                            >
                                New email address
                            </label>
                            <input
                                id="newEmail"
                                className="pf-modal__input"
                                name="newEmail"
                                type="email"
                                value={form.newEmail || ""}
                                onChange={handleChange}
                                placeholder="you@example.com"
                                autoFocus
                            />
                        </div>
                    )}

                    {field === "email" && codeSent && (
                        <div className="pf-modal__fields">
                            <p className="pf-modal__info">
                                A verification code has been sent to your inbox.
                            </p>
                            <label
                                className="pf-modal__label"
                                htmlFor="verificationCode"
                            >
                                Verification code
                            </label>
                            <input
                                id="verificationCode"
                                className="pf-modal__input"
                                name="verificationCode"
                                value={form.verificationCode || ""}
                                onChange={handleChange}
                                placeholder="Enter code"
                                autoFocus
                            />
                        </div>
                    )}

                    {field === "danger-zone" && (
                        <div className="pf-modal__fields">
                            <p className="pf-modal__warn">
                                This action is permanent and cannot be undone.
                                All your recordings, drafts, and data will be
                                deleted.
                            </p>
                            <label
                                className="pf-modal__label"
                                htmlFor="currentPassword"
                            >
                                Enter your password to confirm
                            </label>
                            <input
                                id="currentPassword"
                                className="pf-modal__input"
                                name="currentPassword"
                                type="password"
                                value={form.currentPassword || ""}
                                onChange={handleChange}
                                placeholder="Your current password"
                                autoFocus
                            />
                        </div>
                    )}

                    {error && (
                        <p className="pf-modal__error" role="alert">
                            {error}
                        </p>
                    )}

                    <div className="pf-modal__actions">
                        <button
                            type="button"
                            className="pf-modal__cancel"
                            onClick={onClose}
                            disabled={submitting}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className={`pf-modal__submit${field === "danger-zone" ? " pf-modal__submit--danger" : ""}`}
                            disabled={submitting}
                        >
                            {submitting
                                ? "Saving…"
                                : field === "danger-zone"
                                  ? "Delete my account"
                                  : field === "email" && !codeSent
                                    ? "Send code"
                                    : "Save changes"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function Profile() {
    const {
        isAuthenticated,
        isLoading,
        loginWithRedirect,
        getAccessTokenSilently,
        user,
    } = useAuth0();

    const [loading, setLoading] = useState(true);
    const [message, setMessage] = useState(null);
    const [modalField, setModalField] = useState(null);
    const [sendingPasswordReset, setSendingPasswordReset] = useState(false);
    const [resetTick, setResetTick] = useState(0);
    const [resetRequestedAt, setResetRequestedAt] = useState(() => {
        if (typeof window === "undefined") return null;
        const raw = localStorage.getItem(RESET_REQUEST_KEY);
        const ts = Number(raw);
        if (!Number.isFinite(ts) || ts <= 0) return null;
        return Date.now() - ts < RESET_COOLDOWN_MS ? ts : null;
    });
    const [profile, setProfile] = useState({
        firstName: "",
        lastName: "",
        email: "",
        role: "",
        plan: "",
        updatedAt: null,
    });

    const apiBase = API_BASE;
    const auth0Domain = process.env.REACT_APP_AUTH0_DOMAIN;
    const auth0ClientId = process.env.REACT_APP_AUTH0_CLIENT_ID;
    const auth0DbConnection =
        process.env.REACT_APP_AUTH0_DB_CONNECTION ||
        "Username-Password-Authentication";

    const authedFetch = useCallback(
        async (path, options = {}) => {
            const token = await getAccessTokenSilently({
                authorizationParams: {
                    audience: process.env.REACT_APP_AUTH0_AUDIENCE,
                },
            });
            return fetch(`${apiBase}${path}`, {
                ...options,
                headers: {
                    "Content-Type": "application/json",
                    ...(options.headers || {}),
                    Authorization: `Bearer ${token}`,
                },
            });
        },
        [apiBase, getAccessTokenSilently]
    );

    const handleSave = useCallback(
        async (field, payload) => {
            try {
                const res = await authedFetch("/profile", {
                    method: "PUT",
                    body: JSON.stringify(payload),
                });
                if (!res.ok) {
                    const text = await res.text();
                    throw new Error(text || "Save failed");
                }
                const data = await res.json();
                setMessage({
                    type: "success",
                    text: `${field.charAt(0).toUpperCase() + field.slice(1)} updated.`,
                });
                setModalField(null);
                setProfile((prev) => ({
                    ...prev,
                    firstName: data.first_name ?? prev.firstName,
                    lastName: data.last_name ?? prev.lastName,
                    email: data.email ?? prev.email,
                    role: normalizeInterviewRole(data.role) || prev.role,
                    updatedAt: data.updated_at ?? prev.updatedAt,
                }));
                if (typeof window !== "undefined") {
                    const detail =
                        data && typeof data === "object"
                            ? data.profile || data.user || data
                            : null;
                    const fallback =
                        payload && typeof payload === "object"
                            ? {
                                  ...(payload.firstName != null
                                      ? { first_name: payload.firstName }
                                      : null),
                                  ...(payload.lastName != null
                                      ? { last_name: payload.lastName }
                                      : null),
                                  ...(payload.role != null
                                      ? { role: payload.role }
                                      : null),
                                  ...(payload.email != null
                                      ? { email: payload.email }
                                      : null),
                              }
                            : null;
                    window.dispatchEvent(
                        new CustomEvent("mooz:profileUpdated", {
                            detail:
                                detail && typeof detail === "object"
                                    ? detail
                                    : fallback,
                        })
                    );
                }
            } catch (err) {
                setMessage({
                    type: "error",
                    text: err?.message || "Save failed.",
                });
            }
        },
        [authedFetch]
    );

    const handleSendPasswordReset = useCallback(async () => {
        try {
            setSendingPasswordReset(true);
            await requestAuth0PasswordResetEmail({
                domain: auth0Domain,
                clientId: auth0ClientId,
                email: profile.email,
                connection: auth0DbConnection,
            });
            try {
                await authedFetch("/profile/password-reset", {
                    method: "POST",
                });
            } catch {
                /* noop */
            }
            const now = Date.now();
            setResetRequestedAt(now);
            try {
                localStorage.setItem(RESET_REQUEST_KEY, String(now));
            } catch {
                /* noop */
            }
            setMessage({ type: "success", text: "Password reset email sent." });
        } catch (err) {
            setMessage({
                type: "error",
                text: err?.message || "Failed to send password reset email.",
            });
        } finally {
            setSendingPasswordReset(false);
        }
    }, [auth0ClientId, auth0DbConnection, auth0Domain, profile.email]);

    // Auto-clear message
    useEffect(() => {
        if (!message) return;
        const t = setTimeout(() => setMessage(null), 4500);
        return () => clearTimeout(t);
    }, [message]);

    useEffect(() => {
        if (!resetRequestedAt) return;
        const interval = setInterval(() => setResetTick((n) => n + 1), 1000);
        const msLeft = RESET_COOLDOWN_MS - (Date.now() - resetRequestedAt);
        if (msLeft <= 0) {
            setResetRequestedAt(null);
            localStorage.removeItem(RESET_REQUEST_KEY);
            clearInterval(interval);
            return;
        }
        const t = setTimeout(() => {
            setResetRequestedAt(null);
            localStorage.removeItem(RESET_REQUEST_KEY);
            clearInterval(interval);
        }, msLeft);
        return () => {
            clearTimeout(t);
            clearInterval(interval);
        };
    }, [resetRequestedAt]);

    // Load profile
    useEffect(() => {
        if (isLoading) return;
        if (!isAuthenticated) {
            loginWithRedirect();
            return;
        }
        let aborted = false;
        (async () => {
            try {
                setLoading(true);
                const res = await authedFetch("/profile", { method: "GET" });
                if (!res.ok) {
                    const text = await res.text();
                    throw new Error(text || "Failed");
                }
                const data = await res.json();
                if (aborted) return;
                setProfile({
                    firstName: data.first_name || "",
                    lastName: data.last_name || "",
                    email: data.email || "",
                    role: normalizeInterviewRole(data.role),
                    plan: data.plan || "",
                    updatedAt: data.updated_at || null,
                });
            } catch (err) {
                if (!aborted)
                    setMessage({
                        type: "error",
                        text: "Failed to load profile.",
                    });
            } finally {
                if (!aborted) setLoading(false);
            }
        })();
        return () => {
            aborted = true;
        };
    }, [isAuthenticated, isLoading, loginWithRedirect, authedFetch]);

    useEffect(() => {
        if (typeof document === "undefined") return;
        document.body.classList.toggle("pf-modal-open", Boolean(modalField));
        return () => {
            document.body.classList.remove("pf-modal-open");
        };
    }, [modalField]);

    if (isLoading || loading) {
        return (
            <div className="pf-loading" aria-live="polite">
                <div className="pf-loading__spinner" aria-hidden="true" />
                <span>Loading profile…</span>
            </div>
        );
    }

    const sections = BASE_SECTIONS;

    const displayName =
        `${profile.firstName} ${profile.lastName}`.trim() || "Moozer";
    const planLabel =
        String(profile.plan || "")
            .toLowerCase()
            .replace(/[\s-]/g, "_") === "pro_ai"
            ? "Pro AI"
            : "Free";
    const initials = displayName
        .split(" ")
        .map((w) => w[0])
        .join("")
        .slice(0, 2)
        .toUpperCase();
    const modalRoot = typeof document !== "undefined" ? document.body : null;
    const isDatabaseUser = Boolean(
        user?.sub && String(user.sub).startsWith("auth0|")
    );
    const isResetCooldown =
        isDatabaseUser &&
        resetRequestedAt &&
        Date.now() - resetRequestedAt < RESET_COOLDOWN_MS;
    const resetMsLeft = isResetCooldown
        ? Math.max(0, RESET_COOLDOWN_MS - (Date.now() - resetRequestedAt))
        : 0;
    const resetMins = Math.floor(resetMsLeft / 60000);
    const resetSecs = Math.floor((resetMsLeft % 60000) / 1000);
    const resetCountdown = isResetCooldown
        ? `${resetMins}:${String(resetSecs).padStart(2, "0")}`
        : "";

    return (
        <div className="pf-page">
            <Toast message={message} onDismiss={() => setMessage(null)} />

            <div className="pf-layout">
                {/* ── Sidebar nav ── */}
                <aside className="pf-sidebar" aria-label="Profile sections">
                    {/* Avatar */}
                    <div className="pf-sidebar__avatar-wrap">
                        <div className="pf-sidebar__avatar" aria-hidden="true">
                            {initials}
                        </div>
                        <div className="pf-sidebar__avatar-info">
                            <p className="pf-sidebar__name">{displayName}</p>
                            <PlanBadge plan={profile.plan} />
                        </div>
                    </div>

                    <nav className="pf-sidebar__nav">
                        {sections.map(({ id, label }) => (
                            <Link
                                key={id}
                                to={`/profile/#${id}`}
                                className="pf-sidebar__link"
                                onClick={(e) => navigateScroll(e, id)}
                            >
                                {label}
                                <IconChevRight />
                            </Link>
                        ))}
                    </nav>
                </aside>

                {/* ── Main content ── */}
                <main className="pf-main">
                    {/* Page header */}
                    <div className="pf-main__header">
                        <div>
                            <p className="pf-main__eyebrow">Account</p>
                            <h1 className="pf-main__title">Your profile</h1>
                            <p className="pf-main__sub">
                                Manage your personal information and account
                                settings.
                            </p>
                        </div>
                        {profile.updatedAt && (
                            <p className="pf-main__updated">
                                Last updated{" "}
                                {new Date(profile.updatedAt).toLocaleDateString(
                                    [],
                                    {
                                        day: "2-digit",
                                        month: "short",
                                        year: "numeric",
                                    }
                                )}
                            </p>
                        )}
                    </div>

                    {/* ── Section: Personal information ── */}
                    <section
                        className="pf-section"
                        aria-labelledby="personal-information"
                    >
                        <div className="pf-section__header">
                            <h2
                                className="pf-section__title"
                                id="personal-information"
                            >
                                Personal information
                            </h2>
                        </div>
                        <div className="pf-section__body">
                            <FieldRow
                                label="Display name"
                                value={`${profile.firstName} ${profile.lastName}`.trim()}
                                onEdit={() => setModalField("name")}
                            />
                            <FieldRow
                                label="Interviewing for"
                                hint="The role you're preparing for"
                                value={getInterviewRoleLabel(profile.role)}
                                onEdit={() => setModalField("role")}
                            />
                        </div>
                    </section>

                    {/* ── Section: Account settings ── */}
                    <section
                        className="pf-section"
                        aria-labelledby="account-settings"
                    >
                        <div className="pf-section__header">
                            <h2
                                className="pf-section__title"
                                id="account-settings"
                            >
                                Account settings
                            </h2>
                        </div>
                        <div className="pf-section__body">
                            <FieldRow
                                label="Email address"
                                value={profile.email}
                                editable={false}
                            />
                            <FieldRow
                                label="Password"
                                hint={
                                    isDatabaseUser
                                        ? "Send a reset link to your email"
                                        : "Managed by your login provider"
                                }
                                value="••••••••••••••••••"
                                editable={isDatabaseUser}
                                onEdit={handleSendPasswordReset}
                                actionLabel={
                                    sendingPasswordReset ? "Sending…" : "Reset"
                                }
                                disabled={sendingPasswordReset}
                                actionNode={
                                    isDatabaseUser && isResetCooldown ? (
                                        <span className="pf-field__meta">
                                            Reset requested · {resetCountdown}
                                        </span>
                                    ) : null
                                }
                            />
                            <div className="pf-field">
                                <div className="pf-field__left">
                                    <span className="pf-field__label">
                                        Plan
                                    </span>
                                </div>
                                <div className="pf-field__value">
                                    {planLabel}
                                </div>
                                <Link
                                    to="/pricing"
                                    className="pf-field__btn"
                                    onClick={navigateToTop}
                                >
                                    {planLabel === "Pro AI"
                                        ? "Update membership"
                                        : "Upgrade"}
                                </Link>
                            </div>
                        </div>
                    </section>

                    {/* ── Section: Danger zone ── */}
                    <section
                        className="pf-section pf-section--danger"
                        aria-labelledby="danger-zone"
                    >
                        <div className="pf-section__header">
                            <h2
                                className="pf-section__title pf-section__title--danger"
                                id="danger-zone"
                            >
                                Danger zone
                            </h2>
                        </div>
                        <div className="pf-section__body">
                            <FieldRow
                                label="Delete account"
                                hint="Permanently removes your data. This cannot be undone."
                                value=""
                                editable
                                hideEmpty
                                danger
                                onEdit={() => setModalField("danger-zone")}
                            />
                        </div>
                    </section>
                </main>
            </div>

            {/* ── Edit modal ── */}
            {modalField &&
                modalRoot &&
                createPortal(
                    <EditModal
                        field={modalField}
                        profile={profile}
                        onClose={() => setModalField(null)}
                        onSave={handleSave}
                    />,
                    modalRoot
                )}
        </div>
    );
}
